import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import type { UseFormReturn } from "react-hook-form"

import type {
  AlertSetting,
  AlertSettingType,
  AlertSettingUpsertForm,
} from "types/alerts.types"

import { useUpdateTowerAlertSettingMutation } from "features/api"
import { selectCurrentOrgId } from "features/store/orgSlice"
import { formatMutationParams } from "helpers/utils/alerts"
import { buildGetErrorMessage, snackbarMutation } from "helpers/utils/mutations"
import AlertSettingForm from "../AlertSettingForm"

interface UpdateTowerAlertSettingProps {
  selectedAlert: AlertSetting
  onClose: () => void
  types: AlertSettingType[]
  methods: UseFormReturn<AlertSettingUpsertForm, any>
}

const UpdateTowerAlertSetting = ({
  selectedAlert,
  onClose,
  types,
  methods,
}: UpdateTowerAlertSettingProps) => {
  const { t, i18n } = useTranslation()
  const orgId = useSelector(selectCurrentOrgId)
  const [updateTowerAlertSetting, { isLoading }] = useUpdateTowerAlertSettingMutation()

  const selectedTypeName = selectedAlert.type
  const selectedType = useMemo(
    () => types?.find((type) => type.name === selectedTypeName),
    [types, selectedTypeName],
  )

  const onSubmit = async (data: AlertSettingUpsertForm) => {
    const formattedData = formatMutationParams(
      data,
      selectedType,
      i18n.resolvedLanguage || "en",
    )
    snackbarMutation({
      mutation: updateTowerAlertSetting({
        id: selectedAlert.id,
        orgId: Number(orgId),
        towerId: selectedAlert.entity_id,
        ...formattedData,
      }).unwrap(),
      getErrorMessage: buildGetErrorMessage(
        t("error.UPDATING_ITEM", { item: t("alert_settings.ALERT_SETTING") }),
      ),
      getSuccessMessage: () =>
        t("success.UPDATING_ITEM", {
          item: t("alert_settings.ALERT_SETTING"),
          count: 1,
          context: "female",
        }),
    })
      .then(() => {
        onClose()
      })
      .catch(() => {})
  }

  return (
    <AlertSettingForm
      entity="tower"
      entityId={selectedAlert.entity_id}
      isLoading={isLoading}
      title="alert_settings.EDIT_ALERT_SETTING"
      onSubmit={methods.handleSubmit(onSubmit)}
      methods={methods}
      types={types}
      selectedTypeName={selectedTypeName}
      onClose={onClose}
      buttonText="generic.UPDATE"
      disableType
    />
  )
}

export default UpdateTowerAlertSetting
